<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { TaskStatus } from "@/features/tasks/task.enum";
import { Archive, Check, CheckCircle, CheckCircleBroken } from "utruckin-icons";
// import { CircleCheck } from "lucide-vue-next";
import { IDropdown } from "@/types/main";

type StatusType = keyof typeof TaskStatus;

interface IStatusBadgeProps {
  status: string;
  label?: string;
  showActions?: boolean;
  isDisabled?: boolean;
  arrow?: boolean;
  items: IDropdown[];
  tooltipContent?: string;
  position: "top" | "bottom";
}

interface IStatusBadgeEmits {
  (e: "next"): void;

  (e: "confirm"): void;

  (e: "selected", v: IDropdown): void;
}

const props = withDefaults(defineProps<IStatusBadgeProps>(), {
  showActions: true,
  isDisabled: false,
  arrow: true,
  position: "bottom",
  items: () => [],
});

const emit = defineEmits<IStatusBadgeEmits>();

const isOpen = ref(false);
const rootRef = ref<HTMLElement | null>(null);
const listStyle = ref({
  top: "0px",
  left: "0px",
  width: "200px",
  transformOrigin: "top",
});

const displayLabel = computed(() => {
  if (props.label) return props.label;
  const labels: Record<StatusType, string> = {
    NEW: "New",
    INFORM: "Inform",
    IN_PROGRESS: "In Progress",
    ON_HOLD: "On Hold",
    DONE: "Done",
    QA_FAILED: "Qa Failed",
    CLOSED: "Closed",
  };
  return labels[props.status];
});

// const hasArrow = computed(() => {
//   return (
//     props.showActions &&
//     props.arrow &&
//     ["NEW", "IN_PROGRESS"].includes(props.driver-status)
//   );
// });

const hasTick = computed(() => {
  return props.showActions && ["IN_PROGRESS"].includes(props.status);
});

const hasDropdown = computed(() => {
  return (
    props.showActions &&
    [
      "NEW",
      "INFORM",
      "IN_PROGRESS",
      "ON_HOLD",
      "QA_FAILED",
      "CLOSED",
      "DONE",
    ].includes(props.status)
  );
});

const displayDropdownItem = (name: string) => {
  const labels: any = {
    NEW: "New",
    INFORM: "Inform",
    IN_PROGRESS: "In Progress",
    ON_HOLD: "On Hold",
    DONE: "Done",
    QA_FAILED: "Qa Failed",
    CLOSED: "Closed",
    ARCHIVE: "Archive",
    REQUEST_CLOSE: "Request Close",
  };

  return labels[name];
};

const onNext = () => {
  if (props.isDisabled) return;
  emit("next");
};

const onConfirm = (e: any) => {
  e.stopPropagation();
  if (props.isDisabled) return;
  emit("confirm");
};

function updateListPosition() {
  if (!rootRef.value) return;
  const rect = rootRef.value.getBoundingClientRect();
  listStyle.value = {
    width: `${rect.width}px`,
    left: `${rect.left + window.scrollX}px`,
    top:
      props.position === "top"
        ? `${rect.top + window.scrollY - 6}px`
        : `${rect.bottom + window.scrollY + 6}px`,
    transformOrigin: props.position === "top" ? "bottom" : "top",
  };
}

const toggle = (e: any) => {
  e.stopPropagation();
  if (props.isDisabled) return;
  if (!props.showActions) return;
  if (!hasDropdown.value) return;
  if (!isOpen.value) {
    updateListPosition();
  }
  isOpen.value = !isOpen.value;
};

function choose(item: IDropdown) {
  emit("selected", item);
  isOpen.value = false;
}

function onDocumentClick(e: MouseEvent) {
  const el = rootRef.value;
  if (!el) return;
  const target = e.target as Node | null;
  if (!target) return;
  if (!el.contains(target)) {
    isOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener("click", onDocumentClick);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", onDocumentClick);
});
</script>

<template>
  <div class="c-status-badge" ref="rootRef">
    <div class="badge-wrpper">
      <div
        class="badge"
        :class="[status]"
        v-tooltip="{
          content: tooltipContent,
          html: true,
        }"
        @click="toggle"
      >
        <span class="badge__label">{{ displayLabel }}</span>
      </div>
      <!--      <template v-if="hasArrow">-->
      <!--        <button-->
      <!--          class="action-btn"-->
      <!--          :class="[driver-status, { disabled: isDisabled }]"-->
      <!--          @click="onNext"-->
      <!--          aria-label="Next driver-status"-->
      <!--        >-->
      <!--          <ChevronRight :size="18" />-->
      <!--        </button>-->
      <!--      </template>-->
    </div>

    <template v-if="hasTick">
      <button
        class="action-btn check-btn"
        :class="{ disabled: isDisabled }"
        @click="onConfirm"
        aria-label="Confirm"
      >
        <Check :size="18" />
      </button>
    </template>

    <Teleport to="body">
      <div
        v-if="isOpen"
        class="c-co-dropdown__list"
        :style="{
          position: 'fixed',
          top: listStyle.top,
          left: listStyle.left,
          width: listStyle.width,
          transform: position === 'top' ? 'translateY(-100%)' : 'none',
        }"
      >
        <div
          v-for="(item, index) in items"
          :key="item.value || index"
          class="c-co-dropdown__item"
          @click.stop="choose(item)"
          :class="item.value == 'archive' ? 'archive' : ''"
        >
          <span
            v-if="item.color"
            class="color-dot"
            :style="{ backgroundColor: `var(${item.color})` }"
          />
          <span
            style="display: flex; align-items: center"
            v-if="item.value == 'archive'"
          >
            <Archive :size="16" />
          </span>
          <span
            v-if="item.value === 'request_close'"
            style="display: flex; align-items: center"
          >
            <CheckCircleBroken :size="16" />
          </span>
          <span class="item-text">{{ displayDropdownItem(item.name) }}</span>
          <Check v-if="item.value === modelValue" class="check" />
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
.c-status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  .badge-wrpper {
    display: flex;
    align-items: center;
    gap: 2px;
  }

  .badge,
  .action-btn {
    display: inline-flex;
    align-items: center;
    padding: 4px 8px;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    border-radius: 8px;

    &__label {
      background: transparent;
      color: var(--color-label);
    }

    &.NEW {
      background: var(--color-blue-light);
    }

    &.ON_HOLD {
      background: var(--color-yellow-light);
    }

    &.IN_PROGRESS {
      background: var(--color-orange-light);
    }

    &.CLOSED {
      background: var(--color-green-light);
    }

    &.INFORM {
      background: var(--color-grey-light);
    }

    &.QA_FAILED {
      background: var(--color-red-light);
    }

    &.DONE {
      background: var(--color-green-light);
    }
  }

  .action-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    color: var(--color-label);
    cursor: pointer;
    border-radius: 0 8px 8px 0;

    &.disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  .check-btn {
    background: var(--color-secondary-system-background);
    border-radius: 8px;
  }
}
</style>

<style lang="scss">
.c-co-dropdown__list {
  width: max-content !important;

  .c-co-dropdown__item {
    &.archive {
      background: var(--color-red-lighter);
    }

    .item-text {
      color: var(--color-label);
    }
  }
}
</style>
